import { AxiosError, AxiosResponse } from 'axios';
import { MessageUtils } from '../errors';
import { IApiResponse, IErrorResponseItem, IHttpCommonErrorIndicator } from '../http';
import { NAVIGATE_ROUTE } from '../routes/common-route';
import { commonErrorSignal, initCommonError } from './CommonErrorHandler';
import { BusinessExceptionProps, Command, CommonErrorType, HttpStatusCode } from './ErrorHandleModel';

/**
 *
 * @param {AxiosResponse<unknown, any>} error The http response error.
 * @param {boolean} abortRequest Flag to decision continute or not.
 * @return {boolean} return abort flag.
 */
export function handleError<T>(error: AxiosResponse<unknown> | undefined) {
  const dataCommunication = {
    command: Command.NONE,
    path: '',
    error,
  } as unknown as IHttpCommonErrorIndicator<IErrorResponseItem>;
  const data = error?.data as IApiResponse<T>;
  let abortRequest: boolean = true;
  switch (error?.status) {
    case HttpStatusCode.Forbidden: {
      abortRequest = handleForbidden(data, dataCommunication);
      break;
    }
    case HttpStatusCode.Unauthorized:
      abortRequest = true;
      dataCommunication.command = Command.NAVIGATE;
      dataCommunication.path = NAVIGATE_ROUTE.SC014;
      dataCommunication.statusCode = HttpStatusCode.Unauthorized;
      break;
    case HttpStatusCode.NotFound:
      abortRequest = true;
      dataCommunication.command = Command.NAVIGATE;
      dataCommunication.path = NAVIGATE_ROUTE.SC014;
      dataCommunication.statusCode = HttpStatusCode.NotFound;
      break;
    case HttpStatusCode.RequestTimeout:
      abortRequest = true;
      dataCommunication.command = Command.NAVIGATE;
      dataCommunication.path = NAVIGATE_ROUTE.SC014;
      dataCommunication.statusCode = HttpStatusCode.RequestTimeout;
      break;
    case HttpStatusCode.InternalServerError:
      abortRequest = true;
      dataCommunication.command = Command.NAVIGATE;
      dataCommunication.path = NAVIGATE_ROUTE.SC014;
      dataCommunication.statusCode = HttpStatusCode.InternalServerError;
      break;
    case HttpStatusCode.ServiceUnavailable:
      abortRequest = true;
      dataCommunication.command = Command.NAVIGATE;
      dataCommunication.path = NAVIGATE_ROUTE.MAINTENANCE;
      break;

    case HttpStatusCode.Conflict:
      abortRequest = handleConflictRequest(data, dataCommunication);
      break;
    case HttpStatusCode.BadRequest:
      abortRequest = handleBadRequest(data, dataCommunication);
      break;
    default:
      abortRequest = handleInternalServerError(data, dataCommunication);
      break;
  }

  if (dataCommunication?.command !== Command.NONE) {
    // reset() before set() ensures watch() always fires even if the same error occurs again.
    // Without reset(), signify skips notification when the new value equals the current one
    // (e.g. repeated 500 errors after bfcache restore).
    commonErrorSignal.reset();
    commonErrorSignal.set(dataCommunication);
  }

  return abortRequest;
}

/**
 * Handle Forbidden request. HTTP status 403
 * @param {IApiResponse<T>} error The http response error.
 * @param {IHttpCommonErrorIndicator<IErrorResponseItem>} dataCommunication Flag to decision continute or not.
 * @return {boolean} return abort flag.
 */
function handleForbidden<T>(error: IApiResponse<T>, dataCommunication: IHttpCommonErrorIndicator<IErrorResponseItem>) {
  const err = error?.error;
  if (err?.code === CommonErrorType.OUT_OF_SERVICE_TIME_EXCEPTION) {
    dataCommunication.command = Command.NAVIGATE;
    dataCommunication.path = NAVIGATE_ROUTE.SERVICETIME;

    return true;
  }

  if (err?.code === CommonErrorType.BAD_CREDENTIALS_EXCEPTION) {
    dataCommunication.command = Command.NAVIGATE;
    dataCommunication.path = NAVIGATE_ROUTE.UNAUTHORIZATION;

    return true;
  }

  // Default: navigate to SC014 with 403 status code
  dataCommunication.command = Command.NAVIGATE;
  dataCommunication.path = NAVIGATE_ROUTE.SC014;
  dataCommunication.statusCode = HttpStatusCode.Forbidden;

  return true;
}

/**
 * Handle Bad request. HTTP status 400.
 * @param {IApiResponse<T>} error The http response error.
 * @param {IHttpCommonErrorIndicator<IErrorResponseItem>} dataCommunication Flag to decision continute or not.
 * @return {boolean} return abort flag.
 */
function handleBadRequest<T>(error: IApiResponse<T>, dataCommunication: IHttpCommonErrorIndicator<IErrorResponseItem>) {
  const err = error?.error;
  if (err?.code === CommonErrorType.BUSINESS_EXCEPTION) {
    dataCommunication.command = Command.SHOW_DIALOG;
    dataCommunication.error = {
      ...err,
      field: [],
    };
  }

  return false;
}

/**
 * Handle Conflict request. HTTP status 409.
 * @param {IApiResponse<T>} error The http response error.
 * @param {IHttpCommonErrorIndicator<IErrorResponseItem>} dataCommunication Flag to decision continute or not.
 * @return {boolean} return abort flag.
 */
function handleConflictRequest<T>(
  error: IApiResponse<T>,
  dataCommunication: IHttpCommonErrorIndicator<IErrorResponseItem>
) {
  const err = error?.error;
  if (err?.code === CommonErrorType.RESOURCE_DUPLICATED_EXCEPTION) {
    dataCommunication.command = Command.SHOW_DIALOG;
    dataCommunication.error = {
      ...err,
      field: [],
    };
  }

  return false;
}

/**
 * Handle Internal Server Error. HTTP status 500.
 * @param {IApiResponse<T>} error The http response error.
 * @param {IHttpCommonErrorIndicator<IErrorResponseItem>} dataCommunication Flag to decision continute or not.
 * @return {boolean} return abort flag.
 */
function handleInternalServerError<T>(
  _: IApiResponse<T>,
  dataCommunication: IHttpCommonErrorIndicator<IErrorResponseItem>
) {
  dataCommunication.command = Command.NAVIGATE;
  dataCommunication.path = NAVIGATE_ROUTE.SYSTEM_ERROR;

  return true;
}

/**
 * Unhandle exception.
 * @param {IApiResponse<T>} error The http response error.
 * @param {IHttpCommonErrorIndicator<IErrorResponseItem>} dataCommunication Flag to decision continute or not.
 * @return {boolean} return abort flag.
 */
export function unhandledException(error: AxiosError) {
  const signalData = {
    ...initCommonError,
    error: {
      code: '',
      message: '',
      details: [],
      field: [],
      body: [],
    },
  };

  if (error?.code && error?.code === CommonErrorType.ECONNABORTED) {
    signalData.command = Command.SHOW_DIALOG;
    signalData.error.code = 'PC00_E032';
    signalData.error.message = MessageUtils.get('PC00_E032');
    commonErrorSignal.set(signalData);

    return false;
  }

  if (error?.code && error?.code === CommonErrorType.ERR_NETWORK) {
    signalData.command = Command.NAVIGATE;
    signalData.path = NAVIGATE_ROUTE.SYSTEM_ERROR;
    commonErrorSignal.set(signalData);

    return false;
  }

  signalData.command = Command.NAVIGATE;
  signalData.path = NAVIGATE_ROUTE.SYSTEM_ERROR;
  commonErrorSignal.set(signalData);

  return true;
}

/**
 * Parse error to detail.
 * @param {IApiResponse<T>} error The http response error.
 * @return {BusinessExceptionProps} Biz exception props.
 */
export function errorParse(error: IErrorResponseItem) {
  return {
    errorCode: error?.code,
    errorMsg: error?.message,
    type: MessageUtils.getTypeFromCode(error?.body?.[0]?.code ?? error?.code),
  } as BusinessExceptionProps;
}
